import { Link } from "react-router-dom";
import styled from "styled-components";
import { CheckCircle, Bookmark } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cPath } from "../../constants";
import { normalizeStoreLogoUrl } from "../../utils/storeLogo";
import { hasAdvertisedDiscount } from "../../utils/offerDisplay";
import { media } from "../../styles/media";

const Row = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: center;
  box-sizing: border-box;
  width: 60%;
  max-width: 100%;
  min-width: 0;
  margin-left: auto;
  margin-right: auto;
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.card};
  }
  ${media.md} {
    flex-direction: row;
  }
`;

const DiscountCol = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.border});
  min-height: 0;
  ${media.md} {
    flex-direction: column;
    gap: 0;
    padding: 1.25rem 1.5rem;
    border-bottom: none;
    border-right: 1px solid hsl(${({ theme }) => theme.colors.border});
    min-width: 120px;
  }
`;

const DiscountBig = styled.span`
  font-size: 1.875rem;
  font-weight: 800;
  color: hsl(${({ theme }) => theme.colors.primary});
`;

const OffLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: hsl(${({ theme }) => theme.colors.primary});
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  gap: 1rem;
  min-width: 0;
  ${media.md} {
    flex-direction: row;
    align-items: center;
  }
`;

const Left = styled.div`
  flex: 1;
  min-width: 0;
`;

const StoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const StoreLogo = styled.img`
  width: 3rem;
  height: 3rem;
  border-radius: 0.25rem;
  object-fit: contain;
  flex-shrink: 0;
`;

const StoreName = styled.span`
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  font-weight: 500;
`;

const Exclusive = styled.span`
  font-size: 0.75rem;
  background: hsl(${({ theme }) => theme.colors.couponMaroon} / 0.1);
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: ${({ theme }) => theme.radius.full};
`;

const Title = styled.h3`
  margin: 0;
  font-size: clamp(1rem, 0.92rem + 0.25vw, 1.125rem);
  font-weight: 600;
  color: hsl(${({ theme }) => theme.colors.foreground});
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  ${Row}:hover & {
    color: hsl(${({ theme }) => theme.colors.primary});
  }
  ${media.md} {
    display: block;
    white-space: nowrap;
    text-overflow: ellipsis;
    -webkit-line-clamp: unset;
    -webkit-box-orient: unset;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.375rem;
`;

const TypeTag = styled.span`
  font-size: clamp(0.8125rem, 0.78rem + 0.15vw, 0.875rem);
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Verified = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: clamp(0.8125rem, 0.78rem + 0.15vw, 0.875rem);
  color: hsl(${({ theme }) => theme.colors.primary});
`;

const Used = styled.span`
  font-size: clamp(0.8125rem, 0.78rem + 0.15vw, 0.875rem);
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  flex-shrink: 0;
  ${media.md} {
    align-items: flex-end;
  }
`;

const Cta = styled.div`
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  padding: 0.625rem 1.5rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  font-weight: 600;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  white-space: nowrap;
  transition: opacity 0.15s;
  ${Row}:hover & {
    opacity: 0.9;
  }
`;

const CodeHint = styled.div`
  border: 2px dashed hsl(${({ theme }) => theme.colors.primary} / 0.4);
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 0.5rem 1rem;
  background: hsl(${({ theme }) => theme.colors.primary} / 0.05);
  font-family: ui-monospace, monospace;
  font-size: clamp(0.8125rem, 0.78rem + 0.15vw, 0.875rem);
  letter-spacing: 0.05em;
  text-align: center;
`;

export default function CouponCard({ coupon }) {
  const { t } = useTranslation();
  const storeLogoUrl = normalizeStoreLogoUrl(coupon.storeLogo);
  const imageUrl =
    coupon.productImage ||
    (coupon.imageUrl && coupon.imageUrl !== coupon.storeLogo ? coupon.imageUrl : "") ||
    storeLogoUrl;

  const hasDiscount = hasAdvertisedDiscount(coupon);
  const d = coupon.discountType === "freeShipping"
    ? t("coupon.freeShip")
    : hasDiscount
      ? coupon.discountValue
      : coupon.type === "code"
        ? t("coupon.seeCoupon")
        : t("coupon.getDeal");
  const showOff = hasDiscount && coupon.discountType !== "freeShipping" && coupon.discountType !== "bogo";
  const typeLabel =
    coupon.type === "code" ? t("coupon.code") : coupon.type === "deal" ? t("coupon.deal") : t("coupon.sale");

  return (
    <Row to={cPath(`coupon/${coupon.id}`)}>
      <DiscountCol>
        <DiscountBig>{d}</DiscountBig>
        {showOff && <OffLabel>{t("coupon.off")}</OffLabel>}
      </DiscountCol>
      <Body>
        <Left>
          <StoreRow>
            {imageUrl ? <StoreLogo src={imageUrl} alt="" /> : null}
            <StoreName>{coupon.storeName}</StoreName>
            {coupon.exclusive && <Exclusive>{t("coupon.exclusive")}</Exclusive>}
          </StoreRow>
          <Title>{coupon.title}</Title>
          <Meta>
            <TypeTag>{typeLabel}</TypeTag>
            {coupon.verified && (
              <Verified>
                <CheckCircle size={12} strokeWidth={2} /> {t("coupon.verified")}
                {coupon.updatedAt
                  ? ` · ${new Date(coupon.updatedAt).toLocaleDateString()}`
                  : ""}
              </Verified>
            )}
            {coupon.isLiked ? (
              <span
                title={t("coupon.saved")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: "clamp(0.8125rem, 0.78rem + 0.15vw, 0.875rem)",
                  color: "hsl(348 60% 35%)",
                  fontWeight: 500,
                }}
              >
                <Bookmark size={12} strokeWidth={2} fill="currentColor" /> {t("coupon.saved")}
              </span>
            ) : null}
            <Used>{coupon.usedCount.toLocaleString()} {t("coupon.used")}</Used>
          </Meta>
        </Left>
        <Right>
          {coupon.code ? (
            <>
              <Cta>{t("coupon.seeCoupon")}</Cta>
              <CodeHint>{coupon.code.slice(0, 4)}...</CodeHint>
            </>
          ) : (
            <Cta>{t("coupon.getDeal")}</Cta>
          )}
        </Right>
      </Body>
    </Row>
  );
}
